import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Trophy, Crown, ArrowRight, HelpCircle, UsersRound, Save, Edit3 } from 'lucide-react';
import { db, getCollectionPath } from '../../lib/firebase';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { useBackButton } from '../../hooks/useBackButton';
import { SuperBoard, Super8Match } from '../../types';

interface Props {
  board: SuperBoard;
  onClose: () => void;
}

export function SuperBoardManager({ board: initialBoard, onClose }: Props) {
  const [board, setBoard] = useState<SuperBoard>(initialBoard);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, getCollectionPath('super_boards'), initialBoard.id), (docSnap) => {
      if (docSnap.exists()) {
        setBoard({ id: docSnap.id, ...docSnap.data() } as SuperBoard);
      }
    });
    return () => unsub();
  }, [initialBoard.id]);

  const [isClosing, setIsClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPairsExpanded, setIsPairsExpanded] = useState(() => board.players.some(p => !p.trim()));
  const [showHelp, setShowHelp] = useState(false);
  const [tempPlayers, setTempPlayers] = useState<string[]>(board.players);
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    setTempPlayers(board.players);
  }, [board.players]);

  useBackButton(true, onClose);

  const isSuper12 = board.type === 'super12';
  const numPairs = isSuper12 ? 6 : 4;
  const maxPlayers = isSuper12 ? 12 : 8;

  const getPairName = (pairNumber: number) => {
    const p1 = board.players[(pairNumber - 1) * 2];
    const p2 = board.players[(pairNumber - 1) * 2 + 1];
    if (!p1 && !p2) return 'Vacante';
    return [p1, p2].filter(Boolean).join(' / ');
  };

  const handleSavePlayers = async () => {
    try {
      if (JSON.stringify(tempPlayers) === JSON.stringify(board.players)) return;
      await updateDoc(doc(db, getCollectionPath('super_boards'), board.id), {
        players: tempPlayers
      });
    } catch (e) {
      console.error(e);
    }
  };

  // Super8 / Super12 logic
  const superData = (isSuper12 ? board.super12Data : board.super8Data) || { matches: [] };
  const s8Matches = superData.matches || [];

  const fixtureCombinations = useMemo(() => {
    const combos = [];
    const base = Array.from({ length: numPairs - 1 }, (_, i) => i + 2); // [2, 3, ..., numPairs]
    const numRounds = numPairs - 1;
    const matchesPerRound = numPairs / 2;

    for (let r = 0; r < numRounds; r++) {
      const shift = r % base.length;
      const shifted = shift === 0 
        ? base 
        : [...base.slice(-shift), ...base.slice(0, base.length - shift)];
      const currentRoundPlayers = [1, ...shifted];
      
      for (let i = 0; i < matchesPerRound; i++) {
        const pA = currentRoundPlayers[i];
        const pB = currentRoundPlayers[numPairs - 1 - i];
        combos.push({ 
          round: r + 1, 
          p1: Math.min(pA, pB), 
          p2: Math.max(pA, pB) 
        });
      }
    }
    return combos;
  }, [numPairs]);

  const fullFixture = useMemo(() => {
    return fixtureCombinations.map(combo => {
      const id = `${combo.p1}_${combo.p2}`;
      const existing = s8Matches.find(m => m.id === id);
      return { ...combo, ...(existing || { id, pair1Id: combo.p1, pair2Id: combo.p2 }) };
    });
  }, [s8Matches, fixtureCombinations]);

  const rounds = useMemo(() => {
    const grouped = [];
    const numRounds = numPairs - 1;
    for (let r = 1; r <= numRounds; r++) {
      grouped.push({
        number: r,
        matches: fullFixture.filter(m => m.round === r)
      });
    }
    return grouped;
  }, [fullFixture, numPairs]);

  const standings = useMemo(() => {
    const table: Record<number, { pId: number, pj: number, gf: number, gc: number, dif: number }> = {};
    for (let i = 1; i <= numPairs; i++) {
      table[i] = { pId: i, pj: 0, gf: 0, gc: 0, dif: 0 };
    }

    s8Matches.forEach(m => {
      if (m.score1 !== undefined && m.score2 !== undefined && table[m.pair1Id] && table[m.pair2Id]) {
        table[m.pair1Id].pj += 1;
        table[m.pair1Id].gf += m.score1;
        table[m.pair1Id].gc += m.score2;
        table[m.pair1Id].dif = table[m.pair1Id].gf - table[m.pair1Id].gc;

        table[m.pair2Id].pj += 1;
        table[m.pair2Id].gf += m.score2;
        table[m.pair2Id].gc += m.score1;
        table[m.pair2Id].dif = table[m.pair2Id].gf - table[m.pair2Id].gc;
      }
    });

    return Object.values(table).sort((a, b) => {
      if (b.gf !== a.gf) return b.gf - a.gf;
      return b.dif - a.dif; 
    });
  }, [s8Matches, numPairs]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(onClose, 300);
  };

  const handleUpdateMatchScore = async (id: string, s1: string, s2: string) => {
    const sc1 = parseInt(s1, 10);
    const sc2 = parseInt(s2, 10);
    if (isNaN(sc1) || isNaN(sc2)) return;

    setLoading(true);
    try {
      const existingIdx = s8Matches.findIndex(m => m.id === id);
      const newMatches = [...s8Matches];
      
      const reportedByName = 'Organizador';

      if (existingIdx >= 0) {
        newMatches[existingIdx] = { ...newMatches[existingIdx], score1: sc1, score2: sc2, reportedBy: reportedByName, reportedAt: Date.now() };
      } else {
        const combo = fixtureCombinations.find(c => `${c.p1}_${c.p2}` === id);
        if (combo) {
          newMatches.push({
            id, pair1Id: combo.p1, pair2Id: combo.p2, score1: sc1, score2: sc2, reportedBy: reportedByName, reportedAt: Date.now()
          });
        }
      }

      const matchRef = doc(db, getCollectionPath('super_boards'), board.id);
      const dataField = isSuper12 ? 'super12Data' : 'super8Data';
      await updateDoc(matchRef, {
        [`${dataField}.matches`]: newMatches
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleFinishTournament = async () => {
    if (!await window.confirm(`¿Seguro que quieres finalizar el torneo?`)) return;
    
    setLoading(true);
    try {
      const matchRef = doc(db, getCollectionPath('super_boards'), board.id);
      const dataField = isSuper12 ? 'super12Data' : 'super8Data';
      await updateDoc(matchRef, {
        completedAt: Date.now(),
        [`${dataField}.winnerPairId`]: standings[0].pId,
      });

      handleClose();
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const isFinished = !!board.completedAt;

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6" style={{ isolation: 'isolate' }}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isClosing ? 0 : 1 }}
        transition={{ duration: 0.3 }}
        className="absolute inset-0 bg-black/50 backdrop-blur-md"
        onClick={handleClose}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: isClosing ? 0 : 1, scale: isClosing ? 0.95 : 1, y: isClosing ? 20 : 0 }}
        transition={{ duration: 0.3, type: 'spring', bounce: 0.2 }}
        className={`relative w-full max-w-lg border rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[90vh] ${
          isSuper12 
            ? 'from-[#1e3055] via-[#233050] to-[#192540] bg-gradient-to-br border-sky-500/40 shadow-[0_10px_50px_rgba(56,189,248,0.25)]'
            : 'from-[#3d2f5a] via-[#2e2448] to-[#201938] bg-gradient-to-br border-violet-500/40 shadow-[0_10px_50px_rgba(139,92,246,0.25)]'
        }`}
      >
        <div className="flex flex-col p-5 border-b border-white/10 bg-black/20 shrink-0 relative">
          <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
            <button onClick={() => setShowHelp(true)} className={`p-1 rounded-full transition-colors ${isSuper12 ? 'text-sky-400 hover:bg-sky-400/20' : 'text-violet-400 hover:bg-violet-400/20'}`}>
              <HelpCircle size={28} />
            </button>
            <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors text-white/50 hover:text-white">
              <X size={24} />
            </button>
          </div>
          
          <div className="flex items-center justify-center gap-4 px-4 sm:px-8">
            <div className={`w-12 sm:w-20 h-[2px] rounded-full bg-gradient-to-r from-transparent ${isSuper12 ? 'to-sky-400/60' : 'to-violet-500/60'}`}></div>
            <h2 className="text-2xl font-black text-white uppercase tracking-widest text-center">{board.title}</h2>
            <div className={`w-12 sm:w-20 h-[2px] rounded-full bg-gradient-to-l from-transparent ${isSuper12 ? 'to-sky-400/60' : 'to-violet-500/60'}`}></div>
          </div>
        </div>

        <AnimatePresence>
          {showHelp && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
              onClick={() => setShowHelp(false)}
            >
              <motion.div
                initial={{ scale: 0.95, y: 10, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.95, y: 10, opacity: 0 }}
                className={`bg-[#15171d] border rounded-2xl p-5 max-w-sm w-full shadow-2xl relative ${isSuper12 ? 'border-sky-500/30' : 'border-violet-500/30'}`}
                onClick={e => e.stopPropagation()}
              >
                <button onClick={() => setShowHelp(false)} className="absolute top-4 right-4 p-1 hover:bg-white/10 rounded-full text-white/50 hover:text-white">
                  <X size={18} />
                </button>
                <h3 className={`text-lg font-black uppercase tracking-wider mb-4 pr-6 ${isSuper12 ? 'text-sky-400' : 'text-violet-400'}`}>
                  ¿Cómo funciona el {isSuper12 ? 'Super 12' : 'Super 8'}?
                </h3>
                <div className="space-y-4 text-xs text-white/80 leading-relaxed">
                  <p className="flex items-start"><UsersRound size={14} className="inline-block mr-1.5 shrink-0 mt-0.5" /> <span>Juegan {isSuper12 ? '6 parejas (12 jug.)' : '4 parejas (8 jug.)'} todos contra todos.</span></p>
                  <p>🎾 Se usan <strong>{isSuper12 ? '3 canchas' : '2 canchas'}</strong> simultáneas.</p>
                  <p>⚔️ {isSuper12 ? '5 partidos' : '3 partidos'} por pareja ({isSuper12 ? '15' : '6'} en total). Se juega por tiempo o a 1 set.</p>
                  <p>⏳ Pensado para jugarse en un turno de <strong>90 minutos</strong>.</p>
                  <p>⏱️ <strong>¡Cada game suma!</strong> El Organizador puede finalizar el super cuando termine el turno. Quien esté primero en la tabla, ¡gana!</p>
                </div>
                <button onClick={() => setShowHelp(false)} className={`mt-6 w-full py-2.5 rounded-xl font-black uppercase tracking-wider text-white hover:scale-105 transition-all ${isSuper12 ? 'bg-sky-500 hover:bg-sky-400' : 'bg-violet-500 hover:bg-violet-400'}`}>
                  Entendido
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-5 relative">
          <div className="space-y-6">
            
            {/* Jugadores */}
            <div className="bg-black/30 rounded-xl border border-white/10 p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-black text-white uppercase text-xs tracking-widest">Parejas</h3>
                {!isPairsExpanded ? (
                  <button onClick={() => setIsPairsExpanded(true)} className="flex items-center gap-1 text-[10px] uppercase font-bold text-yellow-500 hover:text-yellow-400 bg-white/5 px-2 py-1 rounded">
                    <Edit3 size={12} /> Editar
                  </button>
                ) : (
                  <button onClick={() => { handleSavePlayers(); setIsPairsExpanded(false); }} className="flex items-center gap-1 text-[10px] uppercase font-bold bg-yellow-500 text-black px-2 py-1 rounded shadow-lg hover:bg-yellow-400">
                    <Save size={12} /> Guardar
                  </button>
                )}
              </div>
              
              <AnimatePresence>
                {isPairsExpanded && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }} 
                    animate={{ opacity: 1, height: 'auto' }} 
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-4">
                      {Array(numPairs).fill(0).map((_, pairIdx) => (
                        <div key={pairIdx} className="bg-white/5 border border-white/10 rounded-lg p-2 flex flex-col gap-1.5">
                          <span className="text-[9px] font-black uppercase text-white/50 tracking-wider">Pareja {pairIdx + 1}</span>
                          <div className="flex flex-col gap-1">
                            {[0, 1].map((playerOffset) => {
                              const i = pairIdx * 2 + playerOffset;
                              return (
                                <input
                                  key={i}
                                  ref={el => inputRefs.current[i] = el}
                                  type="text"
                                  value={tempPlayers[i] || ''}
                                  onChange={(e) => {
                                    const n = [...tempPlayers];
                                    n[i] = e.target.value;
                                    setTempPlayers(n);
                                  }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      if (i + 1 < maxPlayers) {
                                        inputRefs.current[i + 1]?.focus();
                                      } else {
                                        inputRefs.current[i]?.blur();
                                        setIsPairsExpanded(false);
                                      }
                                    }
                                  }}
                                  onBlur={handleSavePlayers}
                                  className="w-full bg-black/50 border border-white/5 rounded px-2 py-1.5 text-[10px] sm:text-xs text-white focus:border-yellow-500 focus:bg-black/80 transition-colors outline-none"
                                  placeholder={`Jugador ${playerOffset + 1}...`}
                                />
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Standings section */}
            <div className="w-full">
              <div className="w-full overflow-hidden rounded-xl border border-white/10 bg-black/30">
                <table className="w-full text-left text-sm whitespace-nowrap">
                  <thead className="bg-black/50 uppercase tracking-wider text-[10px] font-black text-white/50">
                    <tr>
                      <th className="px-4 py-3 text-center">#</th>
                      <th className="px-4 py-3">Pareja</th>
                      <th className="px-3 py-3 text-center" title="Partidos Jugados">PJ</th>
                      <th className="px-3 py-3 text-center text-white/80" title="Games a Favor">GF</th>
                      <th className="px-3 py-3 text-center text-white/80" title="Games en Contra">GC</th>
                      <th className="px-3 py-3 text-center text-yellow-400/80" title="Diferencia de Games">DIF</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {standings.map((row, idx) => {
                      const name = getPairName(row.pId);
                      return (
                        <tr key={row.pId} className={`${idx === 0 ? (isSuper12 ? 'bg-sky-500/10' : 'bg-violet-500/10') : 'hover:bg-white/5'} transition-colors`}>
                          <td className="px-4 py-3 text-center font-black">
                            {idx === 0 ? <Crown size={14} className="text-yellow-500 mx-auto" /> : <span className="text-white/40">{idx + 1}</span>}
                          </td>
                          <td className="px-4 py-3 text-[11px] font-bold text-white max-w-[120px] truncate">{name}</td>
                          <td className="px-3 py-3 text-center text-white/70 font-bold">{row.pj}</td>
                          <td className="px-3 py-3 text-center text-white font-black">{row.gf}</td>
                          <td className="px-3 py-3 text-center text-white font-bold">{row.gc}</td>
                          <td className="px-3 py-3 text-center text-yellow-400 font-black">{row.dif > 0 ? `+${row.dif}` : row.dif}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Fixture section */}
            <div className="space-y-6">
              {rounds.map((ronda) => {
                const isRoundComplete = ronda.matches.every(m => m.score1 !== undefined && m.score2 !== undefined);
                
                return (
                  <div key={ronda.number} className="bg-black/20 rounded-xl border border-white/5 p-3">
                    <div className="flex items-center justify-center gap-3 mb-3 px-1">
                      <h4 className="font-black text-white/80 uppercase text-[11px] tracking-widest flex items-center gap-1.5">
                        <span>📅 Ronda {ronda.number}</span>
                      </h4>
                      {isRoundComplete && <span className="bg-green-500/20 text-green-400 text-[9px] px-1.5 py-0.5 rounded border border-green-500/20">Completada</span>}
                    </div>
                    <div className="space-y-2">
                      {ronda.matches.map((m) => {
                        const p1Name = getPairName(m.pair1Id);
                        const p2Name = getPairName(m.pair2Id);
                        
                        return (
                          <MatchRow 
                             key={m.id}
                             id={m.id!}
                             p1Name={p1Name}
                             p2Name={p2Name}
                             p1Id={m.pair1Id}
                             p2Id={m.pair2Id}
                             score1={m.score1}
                             score2={m.score2}
                             reportedBy={m.reportedBy}
                             isFinished={isFinished}
                             onSave={handleUpdateMatchScore}
                             loading={loading}
                             isSuper12={isSuper12}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        {!isFinished && (
          <div className="p-4 bg-black/40 border-t border-white/5">
            <button 
              onClick={handleFinishTournament}
              disabled={loading}
              className={`relative w-full py-3 text-black font-black uppercase text-xs tracking-widest rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg flex justify-center items-center gap-2 overflow-hidden group border border-yellow-300/30 ${
                isSuper12 
                  ? 'bg-gradient-to-r from-yellow-500 via-yellow-200 to-yellow-500 shadow-[0_0_20px_rgba(234,179,8,0.4)]'
                  : 'bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 shadow-[0_0_20px_rgba(234,179,8,0.4)]'
              }`}
            >
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent group-hover:animate-[shimmer_1.5s_infinite]" />
              <Trophy size={16} className="text-black relative z-10" /> 
              <span className="relative z-10">Finalizar {isSuper12 ? 'Super 12' : 'Super 8'}</span>
            </button>
          </div>
        )}
      </motion.div>
    </div>,
    document.body
  );
}

// MatchRow Component
function MatchRow({ id, p1Name, p2Name, p1Id, p2Id, score1, score2, reportedBy, isFinished, onSave, loading, isSuper12 }: any) {
  const [editing, setEditing] = useState(!isFinished && score1 === undefined);
  const [s1, setS1] = useState(score1 !== undefined ? String(score1) : '');
  const [s2, setS2] = useState(score2 !== undefined ? String(score2) : '');
  const [activeField, setActiveField] = useState<1 | 2 | null>(null);

  const hasResult = score1 !== undefined && score2 !== undefined;
  const isChanged = s1 !== (score1 !== undefined ? String(score1) : '') || s2 !== (score2 !== undefined ? String(score2) : '');

  const handleSave = () => {
    if (s1.trim() === '' || s2.trim() === '') return;
    onSave(id, s1, s2);
    setEditing(false);
    setActiveField(null);
  };

  const handleCancel = () => {
    setS1(score1 !== undefined ? String(score1) : '');
    setS2(score2 !== undefined ? String(score2) : '');
    setEditing(false);
    setActiveField(null);
  };

  const startEditing = () => {
    setEditing(true);
    setActiveField(s1 === '' ? 1 : (s2 === '' ? 2 : 1));
  };

  return (
    <div className={`p-3 rounded-xl border flex flex-col gap-2 ${hasResult ? 'bg-black/30 border-white/15' : 'bg-white/5 border-white/5'} transition-all`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 flex flex-col items-center max-w-[40%]">
          <span className="text-[8px] uppercase tracking-widest text-white/50 font-bold mb-1">Pareja {p1Id}</span>
          <span className="text-[10px] text-white font-bold text-center leading-tight">{p1Name}</span>
        </div>

        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button
                type="button"
                onClick={() => {
                  if (isFinished) return;
                  setActiveField(activeField === 1 ? null : 1);
                }}
                className={`w-10 h-10 flex items-center justify-center bg-black/50 border rounded-lg text-white/80 font-black text-sm outline-none transition-all ${
                  activeField === 1 ? (isSuper12 ? 'border-2 border-sky-400 bg-sky-500/10 scale-105 shadow-lg' : 'border-2 border-violet-500 bg-violet-600/10 scale-105 shadow-lg') : 'border-white/20'
                }`}
              >
                {s1 !== '' ? s1 : '-'}
              </button>
              <span className="text-white/20 text-sm font-black">-</span>
              <button
                type="button"
                onClick={() => {
                  if (isFinished) return;
                  setActiveField(activeField === 2 ? null : 2);
                }}
                className={`w-10 h-10 flex items-center justify-center bg-black/50 border rounded-lg text-white/80 font-black text-sm outline-none transition-all ${
                  activeField === 2 ? (isSuper12 ? 'border-2 border-sky-400 bg-sky-500/10 scale-105 shadow-lg' : 'border-2 border-violet-500 bg-violet-600/10 scale-105 shadow-lg') : 'border-white/20'
                }`}
              >
                {s2 !== '' ? s2 : '-'}
              </button>
            </>
          ) : (
            <>
              <div className="w-9 h-9 flex items-center justify-center bg-black/50 border border-white/15 rounded-lg text-white/90 font-black text-base shadow-inner">
                {score1}
              </div>
              <span className="text-white/20 text-xs font-black">-</span>
              <div className="w-9 h-9 flex items-center justify-center bg-black/50 border border-white/15 rounded-lg text-white/90 font-black text-base shadow-inner">
                {score2}
              </div>
            </>
          )}
        </div>

        <div className="flex-1 flex flex-col items-center max-w-[40%]">
          <span className="text-[8px] uppercase tracking-widest text-white/50 font-bold mb-1">Pareja {p2Id}</span>
          <span className="text-[10px] text-white font-bold text-center leading-tight">{p2Name}</span>
        </div>
      </div>

      {!isFinished && editing && activeField && (
        <div className="mt-1 pt-2 border-t border-white/5 relative z-10 animate-in slide-in-from-top-1 fade-in duration-200">
          <div className="flex justify-center gap-1.5">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => {
                  if (activeField === 1) {
                    setS1(String(n));
                    if (s2 === '') setActiveField(2);
                    else setActiveField(null);
                  } else {
                    setS2(String(n));
                    if (s1 === '') setActiveField(1);
                    else setActiveField(null);
                  }
                }}
                className={`flex-1 max-w-[32px] h-8 rounded-lg font-black text-xs transition-all duration-150 active:scale-95 border text-white/90 hover:scale-110 hover:text-white ${
                  isSuper12 
                    ? 'bg-white/10 border-sky-500/30 hover:bg-sky-400 hover:text-black hover:border-sky-300 hover:shadow-[0_0_14px_rgba(56,189,248,0.7)]' 
                    : 'bg-white/10 border-violet-500/30 hover:bg-violet-400 hover:text-white hover:border-violet-300 hover:shadow-[0_0_14px_rgba(139,92,246,0.7)]'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mt-1 border-t border-white/5 pt-2 px-1">
        <span className="text-[8px] uppercase tracking-widest text-white/40">
          {hasResult ? `Cargado por: ${reportedBy || 'Organizador'}` : 'Pendiente'}
        </span>
        
        {!isFinished && (
          editing ? (
            <div className="flex items-center gap-1.5">
              {isChanged && s1 && s2 && (
                <div className={`flex items-center gap-1 mr-1 animate-pulse ${isSuper12 ? 'text-sky-400' : 'text-violet-400'}`}>
                  <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest whitespace-nowrap">Click para guardar</span>
                  <ArrowRight size={12} className="stroke-[3]" />
                </div>
              )}
              {hasResult && <button type="button" onClick={handleCancel} className="px-2 py-1 text-[9px] font-bold uppercase text-white/50 hover:text-white shrink-0">Cancelar</button>}
              <button disabled={loading || !s1 || !s2 || !isChanged} type="button" onClick={handleSave} className={`shrink-0 px-3 py-1 text-white rounded text-[9px] font-black uppercase tracking-wider disabled:opacity-50 ${ isChanged && s1 && s2 ? 'animate-bounce' : '' } ${isSuper12 ? 'bg-gradient-to-r from-violet-600 to-sky-400 hover:scale-105 transition-all' : 'bg-violet-500 hover:bg-violet-400'}`}>Guardar</button>
            </div>
          ) : (
            <button type="button" onClick={startEditing} className="px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-white/40 hover:text-white border border-white/10 hover:border-white/30 rounded transition-all">
              Modificar
            </button>
          )
        )}
      </div>
    </div>
  );
}
